// The Review queue: replies Kim sent on its own, waiting for a Sentinel's verdict.

import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { format } from 'date-fns';
import { Bot, PanelLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarkdownContent } from '@/components/markdown-content';
import { TicketList } from '@/components/shared/ticket-list';
import { toast } from '@/components/ui/sonner';
import { reviewAutomationReply, useAutomationReplies, useAutomationReplyThread } from '@/hooks/use-automation-replies';
import { getApiErrorMessage } from '@/lib/utils';
import { ticketListCollapsedAtom } from '@/hooks/use-sentinel-workspace';
import { ConfidencePill, ProviderBadge, SentDateRange } from '@/components/shared/automation-reply-badges';
import { humanize, providerOf, sentBetween, type DayRange } from '@/lib/automation-replies';
import type { AutomationReply, AutomationReplyFilter } from '@/services/automation-reply.service';
import { ThreadBubble } from './similar-ticket-panel';
import { FeedbackForAiModal } from './workspace-modals';
import { TicketContextPanel } from './ticket-context-panel';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import type { FeedbackVerdict } from './types';


function ReviewPane({
  reply,
  canEdit,
  onReviewed,
  onExpandList,
  compact = false,
}: {
  reply: AutomationReply;
  canEdit: boolean;
  onReviewed: () => void;
  /** Set while the list is collapsed: shows the button that brings it back. */
  onExpandList?: () => void;
  /** Phones: the pane takes the whole width, and the ticket's details are left to the ticket view. */
  compact?: boolean;
}) {
  const thread = useAutomationReplyThread(reply.id);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const submit = async (verdict: FeedbackVerdict, reasons: string[], otherText: string | null) => {
    try {
      await reviewAutomationReply(reply.id, { verdict: verdict === 'yes' ? 'correct' : 'incorrect', reasons, note: otherText });
      toast.success(verdict === 'yes' ? 'Marked as correct' : 'Marked as incorrect');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The review could not be saved.'));
      // Anything but "already reviewed" keeps the reply here to try again.
      if ((error as { response?: { status?: number } }).response?.status !== 409) return;
    }
    setFeedbackOpen(false);
    onReviewed();
  };

  return (
    <>
    <div className={compact ? 'flex min-w-0 flex-1 flex-col' : 'flex min-w-[420px] flex-1 flex-col'}>
      <div
        className={cn(
          'flex shrink-0 items-center gap-3 border-b border-gray-500 bg-white/80 py-3 pl-4 pr-4',
          // Phones: the details wrap under the ticket number instead of breaking it.
          compact ? 'min-h-[60px] flex-wrap gap-y-1 whitespace-nowrap' : 'h-[60px]',
        )}
      >
        {onExpandList && (
          <Button variant="ghost" size="icon-sm" onClick={onExpandList} className="text-gray-900" aria-label="Expand ticket list">
            <PanelLeft className="size-[14px]" />
          </Button>
        )}
        <p className="text-[16px] font-semibold leading-6 text-text-body"># {reply.ticketId}</p>
        <ProviderBadge provider={reply.provider} />
        {reply.channel && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] capitalize text-gray-900">{reply.channel}</span>}
        <span className="ml-auto text-[12px] text-gray-800">Sent {format(new Date(reply.sentAt), 'd MMM, HH:mm')}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-gray-100 pb-6">
        {thread.isLoading ? (
          <p className="px-4 pt-6 text-center text-[13px] text-text-body">Loading conversation</p>
        ) : thread.data?.messages.length ? (
          // Sized like the workspace chat's bubbles; the narrow similar-ticket panel keeps its own.
          thread.data.messages.map((message) => (
            <ThreadBubble
              key={message.id}
              {...message}
              textClassName="text-[13px]"
              bubbleClassName="max-w-[600px] shadow-[0_2px_6px_0_rgba(0,0,0,0.04)]"
            />
          ))
        ) : (
          <p className="px-4 pt-6 text-center text-[12px] text-gray-800">
            {thread.isError ? 'The conversation could not be loaded.' : 'Kim has no copy of this conversation, only the reply it sent.'}
          </p>
        )}

        <section aria-label="Automated reply" className="mx-4 mt-6 rounded-2xl border border-indigo-200 bg-white p-4 shadow-xs">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-indigo-700">
              <Bot className="size-3.5" />
              Sent by Kim automatically
            </span>
            {reply.intents.map((intent) => (
              <span key={intent} className="rounded-full border border-gray-300 px-2 py-0.5 text-[11px] capitalize text-gray-900">
                {humanize(intent)}
              </span>
            ))}
            <span className="ml-auto">
              <ConfidencePill value={reply.confidence} />
            </span>
          </div>
          <MarkdownContent text={reply.message} className="text-[13px] leading-[20px] text-text-body [&_p]:mb-2 [&_p:last-child]:mb-0" />
        </section>
      </div>

      {canEdit && (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-300 bg-white px-4 py-3">
          <p className="text-[13px] text-text-body">Was this automated reply correct?</p>
          <Button onClick={() => setFeedbackOpen(true)} className="text-[13px] font-normal">
            Give feedback
          </Button>
        </div>
      )}
      <FeedbackForAiModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={(verdict, reasons, otherText) => submit(verdict, reasons, otherText)}
        subtitle="Was this automated reply correct?"
        askScope={false}
      />
    </div>
    {/* The ticket's details, as the ticket view shows them. */}
    {!compact && thread.data?.conversationId != null && <TicketContextPanel key={thread.data.conversationId} conversationId={thread.data.conversationId} />}
    </>
  );
}

/** List and review pane for the selected brand's unreviewed automated replies. */
export function AutomationQueue({
  title,
  views,
  canEdit,
}: {
  title: React.ReactNode;
  /** The view chips, shown above the date range. */
  views?: React.ReactNode;
  canEdit: boolean;
}) {
  const [days, setDays] = useState<DayRange>({});
  // Memoised so the list query keeps one key until the days change.
  const filter = useMemo<AutomationReplyFilter>(() => ({ review: 'pending', outcome: 'sent', ...sentBetween(days) }), [days]);
  const { entries, isLoading, refetch, hasMore, loadingMore, loadMore } = useAutomationReplies(filter);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = entries.find((entry) => entry.id === selectedId) ?? null;
  const [listCollapsed, setListCollapsed] = useAtom(ticketListCollapsedAtom);
  // Phones show one pane at a time: the queue, or the reply being reviewed with a way back.
  const isMobile = useIsMobile();
  const expandList = () => (isMobile ? setSelectedId(null) : setListCollapsed(false));

  const items = useMemo(
    () =>
      entries.map((entry) => ({
        id: entry.id,
        subject: `#${entry.ticketId}`,
        // The row's avatar takes its initial; the channel shows in the review pane's header.
        customerName: providerOf(entry.provider).label,
        preview: entry.message,
        category: entry.intents[0] ? humanize(entry.intents[0]) : null,
        timestamp: entry.sentAt,
      })),
    [entries],
  );

  // A reviewed reply leaves the queue; the next one down opens so a Sentinel can keep going.
  const openNext = () => {
    const index = entries.findIndex((entry) => entry.id === selectedId);
    setSelectedId(entries[index + 1]?.id ?? entries[index - 1]?.id ?? null);
  };

  return (
    <div className="flex h-full w-full overflow-hidden bg-white">
      {(isMobile ? selected === null : !listCollapsed) && (
        <TicketList<string>
          className={isMobile ? 'w-full' : undefined}
          items={items}
          title={title}
          isLoading={isLoading}
          selectedId={selectedId}
          onSelect={setSelectedId}
          emptyMessage={days.startDate ? 'No unreviewed replies on these dates.' : 'No automated replies waiting for review.'}
          paging={{ hasMore, isFetchingNextPage: loadingMore, onLoadMore: loadMore }}
          headerActions={
            <Button variant="ghost" size="icon-sm" onClick={() => void refetch()} className="text-gray-900" aria-label="Refresh automated replies">
              <RefreshCw className="size-[14px]" />
            </Button>
          }
          // Below the heading with the view chips; the 280px header has no room for it.
          toolbar={
            <div className="flex flex-col gap-2">
              {views}
              <SentDateRange value={days} onChange={setDays} />
            </div>
          }
          onCollapse={() => setListCollapsed(true)}
        />
      )}
      {selected ? (
        <ReviewPane
          key={selected.id}
          reply={selected}
          canEdit={canEdit}
          onReviewed={openNext}
          onExpandList={isMobile || listCollapsed ? expandList : undefined}
          compact={isMobile}
        />
      ) : isMobile ? null : (
        <div className="relative flex h-full flex-1 flex-col items-center justify-center gap-2">
          {listCollapsed && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={expandList}
              className="absolute left-4 top-4 text-gray-900"
              aria-label="Expand ticket list"
            >
              <PanelLeft className="size-[14px]" />
            </Button>
          )}
          <Bot className="size-8 text-gray-700" />
          <p className="text-[13px] leading-[18px] text-text-body">Choose an automated reply to review</p>
          <p className="text-[12px] text-gray-800">Reviewed replies leave this queue and show their verdict in Automation → Automation logs.</p>
        </div>
      )}
    </div>
  );
}
